import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCardName, getUILoc, getPlayerName } from '../utils/cardUtils.js';
import '../App.css';

const GameInfo = ({ gameState, enhancedLog, onShowLog, onShowRules }) => {
    const [isFlashing, setIsFlashing] = useState(false);
    const [lastProcessedEntryId, setLastProcessedEntryId] = useState(null);
    const timerRef = useRef(null);
    
    // 最新の効果を取得（最大3行）
    const getLatestEffects = useCallback(() => {
        if (!gameState) return [getUILoc('MESSAGES.LOADING')];
        
        const allEntries = enhancedLog && enhancedLog.getFilteredEntries 
            ? enhancedLog.getFilteredEntries('all') 
            : (gameState.game_log || []);

        if (allEntries.length === 0) {
            return [getUILoc('MESSAGES.GAME_START')];
        }
        
        const recent = allEntries.slice(-3);
        return recent.map((entry, index) => {
            // テンプレート方式
            if (entry.messageKey) {
                const params = { ...entry.params };
                if (params.cardId) params.cardName = getCardName(params.cardId);
                if (params.targetCardId) params.targetCardName = getCardName(params.targetCardId);
                if (params.cardIds) params.exposedCardNames = params.cardIds.map(id => getCardName(id)).join(', ');
                if (params.playerType) params.playerName = getPlayerName(params.playerType);
                if (params.sourceZoneKey) params.sourceZone = getUILoc(params.sourceZoneKey);
                if (params.destZoneKey) params.destZone = getUILoc(params.destZoneKey);
                if (params.originalTypeKey) params.originalType = getUILoc(params.originalTypeKey);
                if (params.isHidden) params.cardName = " ---- ";

                const description = getUILoc(entry.messageKey, params);
                const playerName = entry.params?.playerName || (entry.playerId === 'PLAYER1' ? getPlayerName('SELF') : entry.playerId === 'PLAYER2' ? getPlayerName('OPPONENT') : getUILoc('LOG.SYSTEM'));
                
                let sourceName = '';
                if (entry.sourceCardInfo) {
                    const ownerName = getPlayerName(entry.sourceCardInfo.ownerId === 'PLAYER1' ? 'SELF' : 'OPPONENT');
                    const cardName = getCardName(entry.sourceCardInfo.cardId);
                    sourceName = `${ownerName}>${cardName}`;
                } else {
                    sourceName = getUILoc('LOG.SYSTEM');
                }
                return `[${playerName}][${sourceName}] ${description}`;
            }

            // レガシー方式
            const playerName = entry.playerName || getUILoc('LOG.SYSTEM');
            const sourceName = entry.sourceCard || getUILoc('LOG.SYSTEM');
            const description = entry.description || entry.message || `効果 ${index + 1}`;
            
            return `[${playerName}][${sourceName}] ${description}`;
        });
    }, [gameState, enhancedLog]);

    const latestEffects = getLatestEffects();

    // 点滅アニメーションをトリガー
    const triggerFlash = useCallback(() => {        
        if (isFlashing) return;
        setIsFlashing(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setIsFlashing(false);
            timerRef.current = null;
        }, 600);
    }, [isFlashing]);

    // ターン終了を検出して点滅をトリガー
    const checkForTurnEnd = useCallback(() => {        
        if (!enhancedLog || !gameState) return;
        const allEntries = enhancedLog.getFilteredEntries ? enhancedLog.getFilteredEntries('all') : [];
        if (allEntries.length === 0) return;
        
        const latestEntry = allEntries[allEntries.length - 1];
        const entryId = latestEntry.id;
        
        if (entryId === lastProcessedEntryId) return;
        
        // メッセージキーによる確実な判定
        const isTurnTransition = latestEntry.messageKey === 'LOG.TURN_START' || latestEntry.messageKey === 'LOG.TURN_END';
        
        // レガシー判定
        const entryText = (latestEntry.description || latestEntry.message || "").toLowerCase();
        const isLegacyTurnEnd = entryText.includes('turn end') || entryText.includes('turn start') || entryText.includes('ターン終了') || entryText.includes('ターン開始');

        if (isTurnTransition || isLegacyTurnEnd) {
            triggerFlash();
        }
        
        setLastProcessedEntryId(entryId);
    }, [enhancedLog, gameState, lastProcessedEntryId, triggerFlash]);

    useEffect(() => {
        checkForTurnEnd();
    }, [enhancedLog, gameState, checkForTurnEnd]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);
    
    if (!gameState) return <div>{getUILoc('MESSAGES.LOADING')}</div>;

    const { current_turn, round_number = 1, first_player } = gameState;
    const isCurrentPlayerFirst = current_turn === first_player;
    const turnOrder = isCurrentPlayerFirst ? getPlayerName('FIRST') : getPlayerName('SECOND');

    return (
        <div className="game-info">
            <div className="game-info-header">
                <h3>{getUILoc('SCREENS.TITLE')}</h3>
                {onShowRules && (
                    <button className="rules-button" onClick={onShowRules} title={getUILoc('BUTTONS.RULES')}>？</button>
                )}
            </div>
            
            <div className="turn-info-compact">
                <div className="turn-details">
                    <span className="turn-number">{turnOrder} {getUILoc('LABELS.TURN')} {round_number}</span>
                </div>
            </div>
            
            <div className="log-section-full">
                <div 
                    className={`latest-effects-compact clickable-log ${isFlashing ? 'log-flash-turn' : ''}`}
                    onClick={onShowLog}
                    title={getUILoc('LABELS.HISTORY_LOG')}
                >
                    <div className="effects-scroll">
                        {latestEffects.map((effect, index) => (
                            <div key={index} className="effect-line-compact">
                                {effect}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameInfo;
